import React, { useEffect, useState } from "react";

import { Box, Chip, Grid, MenuItem, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../components/Form";
import Modal from "../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Project } from "../../../../../../../models/project";
import { Roles } from "../../../../../../../models/user";
import ProjectService from "../../../../../../../services/project.service";
import RoleService from "../../../../../../../services/role.service";
import { Status } from "../../../../../../../util/enums";
import { handleValidation } from "../../../../../../../util/formValidation";
import { getTypes } from "../types";
import useStyles from "./styles";

type ModalToInviteUserProps = {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onInviteUser: (email: string, roleId: number, typeUser: string, projects: Project[]) => void;
};

export const ModalToInviteUser = ({
  isOpen,
  isLoading,
  onClose,
  onInviteUser,
}: ModalToInviteUserProps) => {
  const { t: translate } = useTranslation();

  const { currentUser, isFMVersion, isMovingVersion } = useAppState();

  const classes = useStyles();
  const [userEmail, setUserEmail] = useState<string>("");
  const [roleSelected, setRoleSelected] = useState<Roles>();
  const [isValidUserEmail, setIsValidUserEmail] = useState<boolean>(false);
  const [typeUser, setTypeUser] = useState<string>("");
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [roles, setRoles] = useState<Roles[]>();
  const [selectedProjects, setSelectedProjects] = useState<Project[]>(
    isFMVersion ? [] : currentUser?.projects || []
  );
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError } = useYupValidation(errors);

  useQuery(
    ["roles.list"],
    () =>
      RoleService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response?.data && response?.data?.length > 0) setRoles(response.data);
      },
    }
  );
  const { isFetching: isFetchingProjects } = useQuery(
    ["projects.list"],
    () =>
      ProjectService.getAllNetwork({
        attributes: ["id", "name", "companyId"],
        companyId: currentUser?.companyId,
        group: ["projects.id"],
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: isFMVersion,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setProjectList(response.data.rows);
      },
    }
  );

  const schema = yup.object().shape({
    userEmail: yup.string().email().required(),
    isFM: yup.boolean(),
    projects: yup.array().when("isFM", {
      is: true,
      then: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number().typeError(translate("thisFieldIsRequired")),
            name: yup.string().typeError(translate("thisFieldIsRequired")),
          })
        )
        .min(1, translate("thisUserNeedsToHaveSomeProjectSelected"))
        .required(translate("thisUserNeedsToHaveSomeProjectSelected")),
    }),
  });

  const onSubmit = () => {
    if (
      isValidUserEmail &&
      roleSelected &&
      roleSelected?.id &&
      typeUser &&
      !fieldHasError("projects")
    ) {
      onInviteUser(userEmail, roleSelected?.id, typeUser, selectedProjects);
    }
  };

  useEffect(() => {
    handleValidation({
      schema,
      validateSyncObject: { userEmail, isFM: isFMVersion, projects: selectedProjects },
      updateErrors: (err) => {
        if (err && err?.length > 0) {
          setIsValidUserEmail(false);
          setErrors(err);

          return;
        }

        if (err?.length === 0) {
          setIsValidUserEmail(true);
          setErrors([]);
        }
      },
    });
  }, [userEmail, selectedProjects]);

  const renderRoles = () => {
    if (roles && roles?.length > 0) {
      return roles?.map((role) => (
        <MenuItem key={role.id} value={role?.id} data-cy={role.name}>
          {role.name}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderTypeUser = () => {
    const types = getTypes(isMovingVersion, isFMVersion);

    return types?.map((type) => (
      <MenuItem data-cy={type.name} key={type.name} value={type.value}>
        {type.name}
      </MenuItem>
    ));
  };

  const renderChipValueProjects = () => {
    return (
      <Box className={classes.chipBoxProject}>
        {selectedProjects?.map((value: Project) => {
          return <Chip key={value.id} label={value.name} className={classes.chipProject} />;
        })}
      </Box>
    );
  };

  return (
    <Modal
      title={translate("inviteUser")}
      open={isOpen}
      className={classes.modalWidth}
      showActionButtons
      actionButtonProps={{
        textSave: translate("inviteUser"),
        onSave: onSubmit,
        saveDisabled: !isValidUserEmail || !roleSelected || !typeUser || fieldHasError("projects"),
        loading: isLoading,
      }}
      handleClose={onClose}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            fullWidth
            label={translate("userEmail")}
            autoComplete="off"
            name="userEmail"
            data-cy="userEmail"
            placeholder="sales@shipeezi.com"
            disabled={isLoading}
            value={userEmail || ""}
            type="email"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setUserEmail(event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            disabled={isLoading}
            label={translate("typeOfUser")}
            name="typeUser"
            value={typeUser}
            onChange={(event: SelectChangeEvent<any>) => setTypeUser(event.target.value)}
            displayEmpty
            placeholder={translate("select")}
            data-cy="typeUser"
          >
            {renderTypeUser()}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            name="role"
            label={translate("role")}
            value={roleSelected?.id}
            disabled={isLoading}
            onChange={(event) => {
              if (roles && roles?.length > 0) {
                const role = roles?.find((role) => role?.id === Number(event.target.value));

                if (role) setRoleSelected(role);
              }
            }}
            displayEmpty
            placeholder={translate("select")}
            data-cy="userRole"
          >
            {renderRoles()}
          </Select>
        </Grid>
        {isFMVersion && (
          <Grid item container direction="row" xs={12} alignItems="center" spacing={2}>
            <Grid item xs={11}>
              <SkeletonComponent isLoading={isFetchingProjects || isLoading}>
                <Select
                  dataCy="selectProjects"
                  className={classes.select}
                  name="projectList"
                  label={translate("projects")}
                  value={
                    selectedProjects && selectedProjects?.length
                      ? selectedProjects?.map((project) => project?.id || -2)
                      : []
                  }
                  onChange={(event: SelectChangeEvent<any>) => {
                    if (event.target.value.includes(0) || event.target.value.includes(-1)) return;
                    const newProjects = projectList
                      ?.filter((project) => event.target.value.includes(project.id))
                      ?.map((project) => ({
                        ...project,
                        companyId:
                          project && project.networks && project.networks.length
                            ? project?.networks[0]?.associatedCompanyId
                            : project?.companyId,
                      }));

                    setSelectedProjects(newProjects);
                  }}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  multiple
                  renderValue={() => renderChipValueProjects()}
                >
                  {selectedProjects.length < projectList?.length && (
                    <MenuItem
                      value={0}
                      onClick={() => {
                        setSelectedProjects(projectList);
                      }}
                    >
                      <span className={classes.listSpan}>
                        <AddCircleOutline className="mr-3" />
                        {translate("addAll")}
                      </span>
                    </MenuItem>
                  )}
                  {selectedProjects.length === projectList?.length && (
                    <MenuItem
                      value={-1}
                      onClick={() => {
                        setSelectedProjects([]);
                      }}
                    >
                      <span className={classes.removeListSpan}>
                        <RemoveCircleOutline className="mr-3" />
                        {translate("removeAll")}
                      </span>
                    </MenuItem>
                  )}
                  {projectList.map((project) => (
                    <MenuItem data-cy={project.name} value={project.id} key={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>
            {projectList.length > 0 && !(isFetchingProjects || isLoading) && (
              <Grid
                item
                xs={1}
                sm={1}
                md={1}
                lg={1}
                direction="row"
                alignItems="flex-end"
                className={classes.chipMargin}
              >
                <Chip label={selectedProjects.length} className={classes.chipLabelProject} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};
